import { Stack, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useMarkChatAsResolved, useSendChatMessage } from "controller";
import { fetchChatPage } from "controller/hooks/chat/useChat";
import { useGetChatMembers } from "controller/hooks/chat/useChatMembers";
import { addThreadReplyToCache } from "controller/hooks/chat/useThreadMessages";
import { useGlobalStore } from "controller/store/globalStore";
import { useAtomValue, useSetAtom } from "jotai";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { VirtuosoHandle } from "react-virtuoso";
import { PinnedChatInputSection } from "./PinnedChatInputSection";
import { ChatMessageList } from "shared/features/chat/ChatMessageList";
import { useDefaultChatSendMode } from "shared/features/chat/hooks/useDefaultChatSendMode";
import {
  CHAT_PAGE_SIZE,
  chatIsUploadingImages,
  chatSendMode,
  chatThreadParentMessage,
  clearChatReplyToMessage,
  clearChatThreadParentMessage,
  resetChatStore,
  setChatMembers,
  setChatReplyToMessage,
  setChatThreadParentMessage,
  useChatMessageStore,
} from "shared/features/chat/store";
import type { ChatMessageInputRef } from "shared/features/chat/TipTapChatInput";
import { buildFlatChatList } from "shared/features/chat/utils/flattenForVirtuoso";
import { stripHtml } from "shared/features/chat/utils/stripHtml";
import type {
  AccessSpecifier,
  ChatResultItem,
  ThreadMessage,
} from "shared/types";
import { debugConsole } from "utilz/helpers";
import { ChatHeader } from "./ChatHeader";
import { PinnedThreadView } from "./PinnedThreadView";

// ─── Helpers ──────────────────────────────────────────────────────────

function toThreadMessage(savedChat: ChatResultItem): ThreadMessage {
  return {
    _id: savedChat._id,
    chat_id: savedChat.chat_id,
    message: savedChat.message,
    thread_id: savedChat.thread_id ?? "",
    excerpt: savedChat.excerpt ?? null,
    access_specifier: savedChat.access_specifier,
    sender: savedChat.sender,
    attachments: savedChat.attachments ?? [],
    created_at: savedChat.created_at,
    message_platform: savedChat.message_platform,
    message_type: savedChat.message_type,
    editing: savedChat.editing,
    mark_as_resolved: savedChat.mark_as_resolved,
    send_as_email: savedChat.send_as_email,
  };
}

function handleMqttThreadReply(
  savedChat: ChatResultItem,
  updateMessage: (
    id: string,
    updater: (msg: ChatResultItem) => ChatResultItem,
  ) => void,
  openThreadId: string | null | undefined,
  queryClient: import("@tanstack/react-query").QueryClient,
) {
  const parentId = savedChat.parent_message_id;
  if (!parentId) return;
  if (!savedChat.thread_id) return;
  if (savedChat.is_thread_root) return;

  updateMessage(parentId, (parent) => ({
    ...parent,
    is_thread_root: true,
    thread_id: savedChat.thread_id ?? parent.thread_id,
    thread_reply_count: (parent.thread_reply_count ?? 0) + 1,
    thread_last_reply_at: savedChat.created_at,
    excerpt: stripHtml(savedChat.message).slice(0, 500),
    excerpt_sender_name: savedChat.sender?.display_name,
    excerpt_created_at: savedChat.created_at,
  }));

  if (openThreadId && openThreadId === savedChat.thread_id) {
    addThreadReplyToCache(
      queryClient,
      openThreadId,
      toThreadMessage(savedChat),
    );
  }
}

// ─── Pin actions are excluded in pinned chat ─────────────────────────

const EMPTY_PINNED_SET = new Set<string>();

// ─── Component ────────────────────────────────────────────────────────

interface PinnedChatPanelProps {
  serviceType: "ticket" | "task";
  serviceId: string;
  isDisabled: boolean;
  mqttMessage: ChatResultItem | null;
  onMinimize: () => void;
}

export const PinnedChatPanel = memo(function PinnedChatPanel({
  serviceType,
  serviceId,
  isDisabled,
  mqttMessage,
  onMinimize,
}: PinnedChatPanelProps) {
  const theme = useTheme();
  const resourceId = useGlobalStore((state) => state.resourceId);
  const queryClient = useQueryClient();

  const editorRef = useRef<ChatMessageInputRef>(null);
  const virtuosoRef = useRef<VirtuosoHandle>(
    null,
  ) as React.RefObject<VirtuosoHandle>;
  const fetchingPagesRef = useRef<Set<number>>(new Set());
  const prevFlatLengthRef = useRef(0);

  // ─── Message Store (local per instance) ────────────────────────────
  const store = useChatMessageStore();
  const {
    sortedMessages,
    lastAction,
    reachedOldest,
    reachedOldestRef,
    isLoadingInitial,
    isLoadingMore,
    setIsLoadingInitial,
    setIsLoadingMore,
    addPageResult,
    addMqttMessage,
    updateMessage,
    reset: resetStore,
    getNextOlderPage,
  } = store;

  // ─── Jotai Atoms (scoped via Provider in ChatBox) ──────────────────
  const sendMessageAccessSpecifier = useAtomValue(chatSendMode);
  const isUploadingImages = useAtomValue(chatIsUploadingImages);
  const threadParentMessage = useAtomValue(chatThreadParentMessage);
  const setThreadParent = useSetAtom(setChatThreadParentMessage);
  const clearThreadParent = useSetAtom(clearChatThreadParentMessage);
  const setReplyTo = useSetAtom(setChatReplyToMessage);
  const clearReplyAtom = useSetAtom(clearChatReplyToMessage);
  const setAtomMembers = useSetAtom(setChatMembers);
  const resetChatAtoms = useSetAtom(resetChatStore);
  useDefaultChatSendMode();

  // ─── Local State ───────────────────────────────────────────────────
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [highlightedChatId] = useState<string | null>(null);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // ─── Thread view toggle ────────────────────────────────────────────
  const isThreadView = threadParentMessage !== null;

  // ─── Mutations ─────────────────────────────────────────────────────
  const { mutateAsync: sendMessage } = useSendChatMessage();
  const { mutateAsync: markAsResolved } = useMarkChatAsResolved();

  // ─── Fetch chat members ────────────────────────────────────────────
  const { data: chatMembersData } = useGetChatMembers(serviceId);

  useEffect(() => {
    if (chatMembersData) {
      setAtomMembers(chatMembersData);
    }
  }, [chatMembersData, setAtomMembers]);

  // ─── Flat chat list for Virtuoso ───────────────────────────────────
  const flatChatList = useMemo(
    () => buildFlatChatList(sortedMessages),
    [sortedMessages],
  );

  const INITIAL_FIRST_INDEX = 100000;
  const [firstItemIndex, setFirstItemIndex] = useState(INITIAL_FIRST_INDEX);

  // Adjust firstItemIndex when items are prepended (older pages loaded)
  useEffect(() => {
    const currentLength = flatChatList.length;
    const prevLength = prevFlatLengthRef.current;

    if (prevLength > 0 && currentLength > prevLength) {
      const diff = currentLength - prevLength;
      if (lastAction === "prepend" || lastAction === "window") {
        setFirstItemIndex((prev) => prev - diff);
      }
    }

    prevFlatLengthRef.current = currentLength;
  }, [flatChatList, lastAction]);

  // ─── Initial page load ─────────────────────────────────────────────
  useEffect(() => {
    if (!serviceId) return;
    if (!resourceId) return;

    resetStore();
    setFirstItemIndex(INITIAL_FIRST_INDEX);
    prevFlatLengthRef.current = 0;
    fetchingPagesRef.current.clear();

    const loadInitialPage = async () => {
      try {
        const data = await fetchChatPage({
          serviceType,
          serviceId,
          fetcherId: resourceId,
          page: 1,
          limit: CHAT_PAGE_SIZE,
        });
        addPageResult(1, data, CHAT_PAGE_SIZE);
      } catch (err: unknown) {
        debugConsole(err);
      } finally {
        setIsLoadingInitial(false);
      }
    };

    loadInitialPage();
  }, [
    serviceId,
    resourceId,
    serviceType,
    resetStore,
    addPageResult,
    setIsLoadingInitial,
  ]);

  // ─── MQTT real-time messages ───────────────────────────────────────
  const threadParentRef = useRef(threadParentMessage);
  threadParentRef.current = threadParentMessage;

  useEffect(() => {
    if (!mqttMessage) return;

    addMqttMessage(mqttMessage);
    handleMqttThreadReply(
      mqttMessage,
      updateMessage,
      threadParentRef.current?.thread_id,
      queryClient,
    );
  }, [mqttMessage, addMqttMessage, updateMessage, queryClient]);

  // ─── Cleanup on unmount ────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      resetChatAtoms();
    };
  }, [resetChatAtoms]);

  // ─── Load more (scroll up -> older messages) ──────────────────────
  const handleLoadMore = useCallback(async () => {
    if (reachedOldestRef.current) return;
    if (isLoadingMore) return;

    const nextPage = getNextOlderPage();
    if (nextPage === null) return;
    if (fetchingPagesRef.current.has(nextPage)) return;

    fetchingPagesRef.current.add(nextPage);
    setIsLoadingMore(true);

    try {
      const data = await fetchChatPage({
        serviceType,
        serviceId,
        fetcherId: resourceId || "",
        page: nextPage,
        limit: CHAT_PAGE_SIZE,
      });
      addPageResult(nextPage, data, CHAT_PAGE_SIZE);
    } catch (err: unknown) {
      debugConsole(err);
    } finally {
      fetchingPagesRef.current.delete(nextPage);
      setIsLoadingMore(false);
    }
  }, [
    reachedOldestRef,
    isLoadingMore,
    getNextOlderPage,
    setIsLoadingMore,
    addPageResult,
    serviceType,
    serviceId,
    resourceId,
  ]);

  // ─── Send message ─────────────────────────────────────────────────
  const handleSendMessage = useCallback(
    async ({
      type,
      messageText,
      attachments,
      mentionedUsers,
      mentionedEmails,
      parentMessageId,
      excerpt,
    }: {
      type: AccessSpecifier;
      messageText: string;
      attachments?: Array<{
        mapping_id: string;
        file_type: string;
        url: string;
        original_name: string;
      }>;
      mentionedUsers?: string[];
      mentionedEmails?: string[];
      parentMessageId?: string;
      excerpt?: string;
    }) => {
      if (isSendingMessage) return;
      setIsSendingMessage(true);

      const trimmedValue = messageText.trim();
      if (
        (trimmedValue.length > 0 || (attachments && attachments.length > 0)) &&
        !isDisabled
      ) {
        try {
          await sendMessage({
            message: trimmedValue,
            accessSpecifier: type,
            serviceId,
            serviceType,
            messageType: "normal",
            ...(attachments && attachments.length > 0 && { attachments }),
            ...(mentionedUsers?.length && { mentioned_users: mentionedUsers }),
            ...(mentionedEmails?.length && {
              mentioned_emails: mentionedEmails,
            }),
            ...(parentMessageId && { parent_message_id: parentMessageId }),
            ...(excerpt && { excerpt }),
          });

          if (parentMessageId) {
            clearReplyAtom();
          }

          editorRef.current?.clear();
        } catch (err: unknown) {
          debugConsole(err);
        }
      }
      setIsSendingMessage(false);
    },
    [
      isSendingMessage,
      isDisabled,
      sendMessage,
      serviceId,
      serviceType,
      clearReplyAtom,
    ],
  );

  // ─── Mark as resolved ──────────────────────────────────────────────
  const handleMarkAsResolved = useCallback(
    async (chatId: string, newValue: boolean, _key: string) => {
      try {
        const resolved = await markAsResolved({ chatId, newValue });
        updateMessage(chatId, (msg) => ({
          ...msg,
          mark_as_resolved: resolved.mark_as_resolved,
        }));
      } catch (err: unknown) {
        debugConsole(err);
      }
    },
    [markAsResolved, updateMessage],
  );

  // ─── Reply click ───────────────────────────────────────────────────
  const handleReplyClick = useCallback(
    (message: ChatResultItem) => {
      setReplyTo({
        chatId: message.chat_id,
        message: message.message,
        senderDisplayName: message.sender.display_name,
      });
    },
    [setReplyTo],
  );

  // ─── Thread click ──────────────────────────────────────────────────
  const handleThreadClick = useCallback(
    (message: ChatResultItem) => {
      setThreadParent(message);
    },
    [setThreadParent],
  );

  const handleCloseThread = useCallback(() => {
    clearThreadParent();
  }, [clearThreadParent]);

  // ─── Input send handler ────────────────────────────────────────────
  const handleInputSend = useCallback(
    ({
      messageText,
      attachmentArray,
      mentionedUsers,
      mentionedEmails,
      parentMessageId,
      excerpt,
    }: {
      messageText: string;
      attachmentArray?: Array<{
        mapping_id: string;
        file_type: string;
        url: string;
        original_name: string;
      }>;
      mentionedUsers?: string[];
      mentionedEmails?: string[];
      parentMessageId?: string;
      excerpt?: string;
    }) => {
      if (!(isDisabled || isSendingMessage || isUploadingImages)) {
        handleSendMessage({
          messageText,
          type: sendMessageAccessSpecifier,
          attachments: attachmentArray,
          mentionedUsers,
          mentionedEmails,
          parentMessageId,
          excerpt,
        });
      }
    },
    [
      isDisabled,
      isSendingMessage,
      isUploadingImages,
      sendMessageAccessSpecifier,
      handleSendMessage,
    ],
  );

  // ─── Render ────────────────────────────────────────────────────────

  return (
    <>
      <ChatHeader
        onMinimize={onMinimize}
        serviceType={serviceType}
        serviceId={serviceId}
        onBack={isThreadView ? handleCloseThread : undefined}
      />

      {isThreadView ? (
        <PinnedThreadView
          serviceType={serviceType}
          serviceId={serviceId}
          isDisabled={isDisabled}
        />
      ) : (
        <Stack
          sx={{
            flex: 1,
            overflow: "hidden",
            backgroundColor: theme.palette.neutral.main,
          }}
          direction="column"
          justifyContent="space-between"
        >
          <ChatMessageList
            flatChatList={flatChatList}
            firstItemIndex={firstItemIndex}
            virtuosoRef={virtuosoRef}
            isMessagesLoading={isLoadingInitial}
            resourceId={resourceId || ""}
            pinnedChatIdSet={EMPTY_PINNED_SET}
            onMarkAsResolved={handleMarkAsResolved}
            onReply={handleReplyClick}
            onThreadClick={handleThreadClick}
            onLoadMore={handleLoadMore}
            hasNextPage={!reachedOldest}
            isFetchingNextPage={isLoadingMore}
            highlightedChatId={highlightedChatId}
          />
          <PinnedChatInputSection
            serviceType={serviceType}
            serviceId={serviceId}
            isDisabled={isDisabled}
            isSendingMessage={isSendingMessage}
            editorRef={editorRef}
            onSend={handleInputSend}
          />
        </Stack>
      )}

    </>
  );
});
